import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, Send, FileText, BarChart3, Loader2, 
  Plus, Mail, MessageSquare
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const Marketing = () => {
  const { token, getAuthHeaders, isCalendarAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isCalendarAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchStats();
  }, [isCalendarAdmin, navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/marketing/stats`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    {
      icon: <Send className="w-12 h-12" />,
      title: 'Campaigns',
      description: 'Create and manage marketing campaigns. Send ad-hoc or scheduled promotions.',
      link: '/marketing/campaigns',
      stats: stats ? `${stats.active_campaigns || 0} active` : null,
      color: 'yellow'
    },
    {
      icon: <FileText className="w-12 h-12" />,
      title: 'Templates',
      description: 'Create reusable message templates for your marketing campaigns.',
      link: '/marketing/templates',
      stats: stats ? `${stats.templates_count || 0} templates` : null,
      color: 'blue'
    },
    {
      icon: <BarChart3 className="w-12 h-12" />,
      title: 'Console',
      description: 'Track delivery status, view send history, and monitor performance.',
      link: '/marketing/console',
      stats: stats ? `${stats.total_sent || 0} sent` : null,
      color: 'green'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800">Marketing</h1>
            <p className="text-gray-600 mt-2">
              Send promotional emails and SMS to your customers
            </p>
          </div>

          {/* Quick Stats */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white rounded-lg shadow p-4 flex items-center">
                <div className="p-3 bg-green-100 rounded-lg mr-4">
                  <Mail className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Messages Sent</p>
                  <p className="text-2xl font-bold text-gray-800">{stats.total_sent || 0}</p>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-4 flex items-center">
                <div className="p-3 bg-yellow-100 rounded-lg mr-4">
                  <Send className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Active Campaigns</p>
                  <p className="text-2xl font-bold text-gray-800">{stats.active_campaigns || 0}</p>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-4 flex items-center">
                <div className="p-3 bg-blue-100 rounded-lg mr-4">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Templates</p>
                  <p className="text-2xl font-bold text-gray-800">{stats.templates_count || 0}</p>
                </div>
              </div>
            </div>
          )}

          {/* Menu Cards */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {menuItems.map((item, index) => (
                <div
                  key={index}
                  onClick={() => navigate(item.link)}
                  className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                  data-testid={`menu-${item.title.toLowerCase()}`}
                >
                  <div className={`text-${item.color}-500 mb-4`}>
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{item.description}</p>
                  {item.stats && (
                    <p className="text-sm text-gray-500 mt-auto">{item.stats}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Quick Action */}
          <div className="mt-8 p-6 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-lg shadow-lg">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="text-white mb-4 md:mb-0">
                <h3 className="text-xl font-bold">Ready to reach your customers?</h3>
                <p className="text-yellow-100">Create a campaign and start engaging today.</p>
              </div>
              <button
                onClick={() => navigate('/marketing/campaigns')}
                className="flex items-center px-6 py-3 bg-white text-yellow-600 rounded-md font-semibold hover:bg-gray-100 transition-colors"
                data-testid="create-campaign-cta"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create Campaign
              </button>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Marketing;
